import { SHOT_LIST_FRAMING_PRESETS, type GeneratedShotRowV2DictYMap, type ShotListRowDictYMap, type ShotSeamDictYMap } from '../../../../generated/nodeDataTypes'
import { ANGLE_LABELS, FOCUS_LABELS, SHOT_LABELS } from './projectGeneratedShots'

export type ShotEditField = 'duration_ms' | 'visual_description' | 'camera_movement' | 'location' |
  'interior_exterior' | 'time_of_day' | 'dialogue_voiceover' | 'sfx' | 'transition' |
  'boundary_type' | 'opening_transition' | 'framing_shot' | 'framing_angle' | 'framing_lens' | 'framing_focus'
export type ShotEditSource = 'guided' | 'screenplay' | 'video' | 'flat'
export type ShotEdit = { source: ShotEditSource; rowId: string; field: ShotEditField; before: string; after: string; beforeVisualPrompt?: string; beforeNeighborTransition?: string; beforeBoundary?: 'Hard' | 'Soft' | 'Continuous'; afterBoundary?: 'Hard' | 'Soft' | 'Continuous' }

/** User-correctable edit failures carry a locale key; unexpected invariants remain ordinary errors. */
export class ShotEditError extends Error {
  constructor(readonly key: `shotList.errors.${string}`, message: string, readonly params: Record<string, string | number> = {}) {
    super(message)
    this.name = 'ShotEditError'
  }
}

const CHOICE_LABELS = {
  shot: SHOT_LABELS, angle: ANGLE_LABELS, focus: FOCUS_LABELS,
}

type FramingChoice = 'shot' | 'angle' | 'focus'
const fieldChoice = (field: ShotEditField): FramingChoice | undefined => {
  if (field === 'framing_shot') return 'shot'
  if (field === 'framing_angle') return 'angle'
  if (field === 'framing_focus') return 'focus'
  return undefined
}

export function readShotField(row: GeneratedShotRowV2DictYMap | ShotListRowDictYMap,
  seams: readonly ShotSeamDictYMap[], index: number, field: ShotEditField): string {
  if (field === 'opening_transition') return seams[0]?.transition ?? ''
  if (field === 'transition' || field === 'boundary_type') {
    if ('generation_id' in row) return (field === 'transition' ? seams[index + 1]?.transition : seams[index + 1]?.boundary_type) ?? ''
    return field === 'transition' ? row.transition_out ?? '' : ''
  }
  if (field === 'duration_ms') return String(row.duration_ms ?? '')
  if (field === 'visual_description') return row.visual_description ?? ''
  if (field === 'camera_movement') return row.camera_movement ?? ''
  if (field === 'location') return row.location ?? ''
  if (field === 'dialogue_voiceover') return row.dialogue_voiceover ?? ''
  if (field === 'sfx') return row.sfx ?? ''
  if ('generation_id' in row) {
    if (field === 'interior_exterior') return row.interior_exterior ?? ''
    if (field === 'time_of_day') return row.time_of_day ?? ''
    const framing = row.framing
    if (field === 'framing_lens') {
      const lens = framing?.lens
      return lens ? `${lens.focal_length_mm} mm / f${lens.aperture_f_number}` : ''
    }
    const choice = fieldChoice(field)
    if (choice) {
      const value = framing?.[choice]
      const id = value?.preset_id
      return value?.custom ?? (id ? CHOICE_LABELS[choice][id] ?? id : '')
    }
  } else {
    if (field === 'framing_shot') return row.framing_shot ?? ''
    if (field === 'framing_angle') return row.framing_angle ?? ''
    if (field === 'framing_lens') return row.framing_lens ?? ''
    if (field === 'framing_focus') return row.framing_focus ?? ''
  }
  throw new Error(`Shot List field ${field} is unavailable on this source`)
}

export function validateShotEdit(source: ShotEditSource, field: ShotEditField, draft: string, requireWholeSeconds = true): string {
  if (field === 'duration_ms') {
    if (source === 'video') throw new ShotEditError('shotList.errors.measuredDuration', 'Measured video shot durations cannot be edited')
    const parsed = /^(\d+)(?:\.(\d{1,3}))?$/.exec(draft.trim())
    const ms = parsed ? Number(parsed[1]) * 1000 + Number((parsed[2] ?? '').padEnd(3, '0')) : NaN
    if (!Number.isSafeInteger(ms) || ms < 1 ||
        (source !== 'flat' && requireWholeSeconds && ms % 1000 !== 0)) {
      throw new ShotEditError('shotList.errors.duration', 'Duration must be a positive number of seconds (whole seconds for generated shots)')
    }
    return String(ms)
  }
  const value = draft.trim()
  if (field === 'visual_description' && !value) throw new ShotEditError('shotList.errors.descriptionRequired', 'A visual description is required')
  if (field === 'boundary_type') {
    if (source === 'flat' || !['Hard', 'Soft', 'Continuous'].includes(value)) {
      throw new ShotEditError('shotList.errors.boundaryChoice', 'Choose Hard, Soft or Continuous for this generated seam')
    }
  }
  if (field === 'opening_transition' && (source === 'flat' || value.toLowerCase() === 'continuous')) {
    throw new ShotEditError('shotList.errors.openingContinuous', 'The opening transition cannot be Continuous')
  }
  if (field === 'framing_lens' && source !== 'flat' && value) {
    const match = /^(\d+)\s*mm\s*\/\s*f(\d+(?:\.\d+)?)$/i.exec(value)
    if (!match || !Number.isSafeInteger(Number(match[1])) || Number(match[1]) < 1 || Number(match[2]) <= 0) {
      throw new ShotEditError('shotList.errors.lensFormat', 'Enter the lens as 35 mm / f2.8, or clear it')
    }
  }
  if (source === 'screenplay' && field.startsWith('framing_') && !value) {
    throw new ShotEditError('shotList.errors.screenplayFraming', 'Screenplay-bound shots require complete framing')
  }
  if (source === 'flat' && (field === 'interior_exterior' || field === 'time_of_day')) {
    throw new ShotEditError('shotList.errors.singleLocation', 'This Shot List has only a single location field')
  }
  return value
}

export function durationSecondsFromMs(milliseconds: number): string {
  if (!Number.isSafeInteger(milliseconds) || milliseconds < 1) throw new Error('Shot duration must be positive integer milliseconds')
  return milliseconds % 1000 === 0 ? String(milliseconds / 1000)
    : `${Math.floor(milliseconds / 1000)}.${String(milliseconds % 1000).padStart(3, '0')}`
}

/** Call within one canvas transaction, after validating and comparing the before value. */
export function writeShotField(row: GeneratedShotRowV2DictYMap | ShotListRowDictYMap,
  seams: readonly ShotSeamDictYMap[], index: number, field: ShotEditField, value: string): void {
  if (field === 'opening_transition') {
    if (!('generation_id' in row) || !seams[0]) throw new Error('Generated opening seam is unavailable')
    seams[0].setTransition(value)
    seams[0].setBoundaryType(value && !['cut', 'cut to', 'hard cut'].includes(value.toLowerCase()) ? 'Soft' : 'Hard')
    return
  }
  if (field === 'transition' || field === 'boundary_type') {
    if ('generation_id' in row) {
      const seam = seams[index + 1]
      if (!seam) throw new Error('Shot List transition seam is missing')
      if (field === 'transition') {
        seam.setTransition(value)
        const token = value.trim().toLowerCase()
        if (!seam.boundary_type || token === 'continuous' ||
            (seam.boundary_type === 'Continuous' && token !== 'continuous')) {
          seam.setBoundaryType(token === 'continuous' ? 'Continuous'
            : /^(soft|soft cut|dissolve|fade|match cut|wipe)$/.test(token) ? 'Soft' : 'Hard')
        }
      }
      else if (value === 'Hard' || value === 'Soft' || value === 'Continuous') seam.setBoundaryType(value)
      else throw new Error('Invalid Shot List boundary')
      return
    }
    if (field === 'boundary_type') throw new Error('Flat Shot Lists do not have boundary types')
    row.setTransitionOut(value)
    return
  }
  if (field === 'duration_ms') { row.setDurationMs(Number(value)); return }
  if (field === 'visual_description') {
    row.setVisualDescription(value)
    if ('generation_id' in row) row.deleteVisualPrompt()
    return
  }
  if (field === 'camera_movement') { row.setCameraMovement(value); return }
  if (field === 'location') { row.setLocation(value); return }
  if (field === 'dialogue_voiceover') { row.setDialogueVoiceover(value); return }
  if (field === 'sfx') { row.setSfx(value); return }
  if ('generation_id' in row) {
    if (field === 'interior_exterior') { row.setInteriorExterior(value); return }
    if (field === 'time_of_day') { if (value) row.setTimeOfDay(value); else row.deleteTimeOfDay(); return }
    if (!row.framing) row.setFraming({})
    const framing = row.framing
    if (!framing) throw new Error('Could not initialize Shot List framing')
    if (field === 'framing_lens') {
      if (!value) framing.deleteLens()
      else {
        const match = /^(\d+)\s*mm\s*\/\s*f(\d+(?:\.\d+)?)$/i.exec(value)
        if (!match) throw new Error('Invalid lens')
        framing.setLens({ focal_length_mm: Number(match[1]), aperture_f_number: Number(match[2]) })
      }
      return
    }
    const choice = fieldChoice(field)
    if (choice) {
      const matchedId = Object.entries(CHOICE_LABELS[choice]).find(([, label]) => label === value)?.[0]
      const preset = SHOT_LIST_FRAMING_PRESETS[choice].some(label => label === value) && matchedId
      const next = preset ? { preset_id: matchedId } : { custom: value }
      if (choice === 'shot') { if (!value) framing.deleteShot(); else framing.setShot(next); return }
      if (choice === 'angle') { if (!value) framing.deleteAngle(); else framing.setAngle(next); return }
      if (!value) framing.deleteFocus(); else framing.setFocus(next)
      return
    }
  } else {
    if (field === 'framing_shot') { if (value) row.setFramingShot(value); else row.deleteFramingShot(); return }
    if (field === 'framing_angle') { if (value) row.setFramingAngle(value); else row.deleteFramingAngle(); return }
    if (field === 'framing_lens') { if (value) row.setFramingLens(value); else row.deleteFramingLens(); return }
    if (field === 'framing_focus') { if (value) row.setFramingFocus(value); else row.deleteFramingFocus(); return }
  }
  throw new Error(`Shot List field ${field} cannot be edited`)
}
